import { useState, useEffect } from 'react';
import { UserPlus, Users, User, X, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import api from '../../lib/api';

const InviteUserDialog = ({
  open,
  onOpenChange,
  workspaceName,
  workspaceId,
  email,
  setEmail,
  role,
  setRole,
  onInvite,
  loading,
  isAdmin = false
}) => {
  const [inviteMode, setInviteMode] = useState('email');
  const [allUsers, setAllUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch users and groups when dialog opens (for admin)
  useEffect(() => {
    if (open && isAdmin) {
      fetchUsersAndGroups();
    }
  }, [open, isAdmin]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setInviteMode('email');
      setSelectedUsers([]);
      setSelectedGroups([]);
      setSearchQuery('');
    }
  }, [open]);

  const fetchUsersAndGroups = async () => {
    setLoadingData(true);
    try {
      // Fetch ALL users (not just first page) and groups
      const [usersRes, groupsRes] = await Promise.all([
        api.get('/admin/users', { params: { limit: 500 } }),
        api.get('/admin/groups').catch(() => ({ data: { groups: [] } }))
      ]);
      setAllUsers(usersRes.data.users || []);
      setUserGroups(groupsRes.data.groups || []);
    } catch (error) {
      console.error('Failed to fetch users/groups:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleGroupToggle = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleBulkInvite = async () => {
    if (selectedUsers.length === 0 && selectedGroups.length === 0) return;
    
    setBulkLoading(true);
    try {
      const response = await api.post(`/workspaces/${workspaceId}/invite-bulk`, {
        user_ids: selectedUsers,
        group_ids: selectedGroups,
        role: role
      });
      onOpenChange(false);
      setSelectedUsers([]);
      setSelectedGroups([]);
      // Return the response for toast message
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredUsers = allUsers.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredGroups = userGroups.filter(g => 
    g.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Regular user view (email invite only)
  if (!isAdmin) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-gray-100">
              <UserPlus className="w-5 h-5 text-[#4A9CC7]" />
              Invite to {workspaceName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email" className="dark:text-gray-300">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                data-testid="invite-email-input"
                className="dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-gray-300">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select" className="dark:bg-slate-900 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="admin">Admin - Can manage workspace</SelectItem>
                  <SelectItem value="member">Member - Can create tasks</SelectItem>
                  <SelectItem value="viewer">Viewer - Read only</SelectItem>
                  <SelectItem value="private_contributor">Private Contributor - Limited visibility</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="dark:border-slate-600 dark:text-gray-300">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={onInvite}
              disabled={loading || !email.trim()}
              className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
              data-testid="invite-submit-btn"
            >
              {loading ? 'Inviting...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Admin view with tabs for email and bulk invite
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg dark:bg-slate-800 dark:border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-gray-100">
            <UserPlus className="w-5 h-5 text-[#4A9CC7]" />
            Invite to {workspaceName}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={inviteMode} onValueChange={setInviteMode} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email">By Email</TabsTrigger>
            <TabsTrigger value="bulk">Select Users/Groups</TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email" className="dark:text-gray-300">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                data-testid="invite-email-input"
                className="dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-gray-300">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select" className="dark:bg-slate-900 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="admin">Admin - Can manage workspace</SelectItem>
                  <SelectItem value="member">Member - Can create tasks</SelectItem>
                  <SelectItem value="viewer">Viewer - Read only</SelectItem>
                  <SelectItem value="private_contributor">Private Contributor - Limited visibility</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="pt-4">
              <DialogClose asChild>
                <Button variant="outline" className="dark:border-slate-600 dark:text-gray-300">Cancel</Button>
              </DialogClose>
              <Button 
                onClick={onInvite}
                disabled={loading || !email.trim()}
                className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                data-testid="invite-submit-btn"
              >
                {loading ? 'Inviting...' : 'Send Invite'}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="bulk" className="space-y-4 py-4">
            {/* Search */}
            <Input
              placeholder="Search users or groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="dark:bg-slate-900 dark:border-slate-600"
            />

            {/* Selected badges */}
            {(selectedUsers.length > 0 || selectedGroups.length > 0) && (
              <div className="flex flex-wrap gap-2 p-2 bg-slate-100 dark:bg-slate-900 rounded-lg">
                {selectedUsers.map(userId => {
                  const user = allUsers.find(u => u.user_id === userId);
                  return (
                    <Badge key={userId} variant="secondary" className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {user?.name || user?.email}
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-red-500" 
                        onClick={() => handleUserToggle(userId)}
                      />
                    </Badge>
                  );
                })}
                {selectedGroups.map(groupId => {
                  const group = userGroups.find(g => g.group_id === groupId);
                  return (
                    <Badge key={groupId} variant="secondary" className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900">
                      <Users className="w-3 h-3" />
                      {group?.name}
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-red-500" 
                        onClick={() => handleGroupToggle(groupId)}
                      />
                    </Badge>
                  );
                })}
              </div>
            )}

            {/* Role selection */}
            <div className="space-y-2">
              <Label className="dark:text-gray-300">Role for all selected</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="admin">Admin - Can manage workspace</SelectItem>
                  <SelectItem value="member">Member - Can create tasks</SelectItem>
                  <SelectItem value="viewer">Viewer - Read only</SelectItem>
                  <SelectItem value="private_contributor">Private Contributor - Limited visibility</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loadingData ? (
              <div className="text-center py-4 text-gray-500">Loading...</div>
            ) : (
              <ScrollArea className="h-[250px] border rounded-lg dark:border-slate-700">
                {/* Groups Section */}
                {filteredGroups.length > 0 && (
                  <div className="p-2">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2 px-2">Groups</div>
                    {filteredGroups.map(group => (
                      <div
                        key={group.group_id}
                        className="flex items-center gap-3 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded cursor-pointer"
                        onClick={() => handleGroupToggle(group.group_id)}
                      >
                        <Checkbox
                          checked={selectedGroups.includes(group.group_id)}
                          onCheckedChange={() => handleGroupToggle(group.group_id)}
                        />
                        <Users className="w-4 h-4 text-blue-500" />
                        <div className="flex-1">
                          <div className="font-medium dark:text-gray-200">{group.name}</div>
                          <div className="text-xs text-gray-500">{group.member_count || 0} members</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Users Section */}
                {filteredUsers.length > 0 && (
                  <div className="p-2">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2 px-2">Users</div>
                    {filteredUsers.map(user => (
                      <div
                        key={user.user_id}
                        className="flex items-center gap-3 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded cursor-pointer"
                        onClick={() => handleUserToggle(user.user_id)}
                      >
                        <Checkbox
                          checked={selectedUsers.includes(user.user_id)}
                          onCheckedChange={() => handleUserToggle(user.user_id)}
                        />
                        <User className="w-4 h-4 text-gray-500" />
                        <div className="flex-1">
                          <div className="font-medium dark:text-gray-200">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {filteredUsers.length === 0 && filteredGroups.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {searchQuery ? 'No users or groups found' : 'No users or groups available'}
                  </div>
                )}
              </ScrollArea>
            )}

            <DialogFooter className="pt-4">
              <DialogClose asChild>
                <Button variant="outline" className="dark:border-slate-600 dark:text-gray-300">Cancel</Button>
              </DialogClose>
              <Button 
                onClick={async () => {
                  try {
                    const result = await handleBulkInvite();
                    if (result) {
                      // Toast will be shown by parent
                      onInvite(result);
                    }
                  } catch (error) {
                    console.error('Bulk invite failed:', error);
                  }
                }}
                disabled={bulkLoading || (selectedUsers.length === 0 && selectedGroups.length === 0)}
                className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
              >
                {bulkLoading ? 'Inviting...' : `Invite ${selectedUsers.length + selectedGroups.length} Selected`}
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserDialog;
